import * as PopoverPrimitive from "@radix-ui/react-popover";
import { withTheme } from "@rjsf/core";
import classNames from "classnames";
import {
  action,
  computed,
  IComputedValue,
  IObservableValue,
  observable,
  reaction,
  runInAction,
} from "mobx";
import { observer } from "mobx-react-lite";
import { nanoid } from "nanoid";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { STATE_PANE_GETTER_OUTPUT_INDEX } from "../Constants";
import {
  ActionType,
  EvalPaneTemplate,
  Pane,
  PaneInput,
  PaneOutput,
  PaneType,
  PaneViewMode,
} from "../Types";
import { assert } from "../Utils";
import { canvasDispatch } from "./CanvasDispatch";
import { activePaneStates } from "./CanvasState";
import { EvalPaneTemplates } from "./EvalPaneTemplates";
import { Evaluator, getFunctionComputed, getPaneInputBox } from "./Evaluator";
import { PaneBody } from "./PaneBody";
import { PaneEvaluateTemplateComponentCanvas } from "./PaneEvaluateTemplateComponentCanvas";
import { PaneTransformButton } from "./PaneHeader";
import { PaneHeaderBar } from "./PaneHeaderBar";
import { PaneInputNode, PopupPaneInputId } from "./PaneInput";
import { isCustomLayoutId } from "./PaneLayout";
import {
  PaneRenderOutputComponent,
  ReactErrorBoundary,
} from "./PaneRenderOutput";
import { PaneSplit } from "./PaneSplit";
import { statePaneGetterValue, statePaneSetterValue } from "./SandboxUtils";

function EjectButton({ paneId }: { paneId: string }) {
  const [open, setOpen] = useState(false);

  return (
    <PopoverPrimitive.Root modal={true} open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild={true}>
        <button
          className={classNames(
            "px-2 py-2 font-medium text-neutral-500 hover:text-primary-600 transition-colors"
          )}
        >
          eject
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side="right"
          sideOffset={8}
          align="start"
          className="bg-neutral-0 rounded-md shadow-lg p-2 popover-animate-in"
        >
          <div className="text-sm font-semibold mb-1">Eject template pane</div>
          <div className="text-neutral-400 mb-4">
            You cannot undo this action. Continue?
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                canvasDispatch([ActionType.EjectTemplatePane, { paneId }]);
              }}
              className="button"
            >
              eject
            </button>
            <button
              onClick={() => {
                setOpen(false);
              }}
              className="text-neutral-400 hover:text-neutral-600 transition-colors"
            >
              cancel
            </button>
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

export const ThemedForm = withTheme({
  children: <div />,
  widgets: {
    TextWidget: (props) => {
      const {
        id,
        readonly,
        disabled,
        autofocus,
        value,
        options,
        placeholder,
        onChange,
        onBlur,
        onFocus,
      } = props;
      return (
        <input
          id={id}
          type="text"
          key={id}
          className="w-full h-6 px-1 border bg-neutral-0 border-neutral-200 hover:border-neutral-300 focus:border-primary-400 rounded-sm"
          readOnly={readonly}
          disabled={disabled}
          autoFocus={autofocus}
          value={value == null ? "" : value}
          placeholder={placeholder}
          onChange={(event) => {
            const value = event.target.value;
            onChange(value === "" ? options.emptyValue : value);
          }}
          onBlur={onBlur && ((event) => onBlur(id, event.target.value))}
          onFocus={onFocus && ((event) => onFocus(id, event.target.value))}
        />
      );
    },
    // https://github.com/rjsf-team/react-jsonschema-form/blob/3ec17f1c0ff40401b7a99c5e9891ac2834a1e73f/packages/core/src/components/widgets/CheckboxWidget.js#L5
    CheckboxWidget: (props) => {
      const {
        schema,
        id,
        value,
        disabled,
        readonly,
        label,
        autofocus,
        onBlur,
        onFocus,
        onChange,
        // @ts-ignore
        DescriptionField,
      } = props;

      return (
        <div className={`${disabled || readonly ? "disabled" : ""}`}>
          {schema.description && (
            <DescriptionField description={schema.description} />
          )}
          <label className="flex items-center">
            <input
              type="checkbox"
              id={id}
              checked={typeof value === "undefined" ? false : value}
              disabled={disabled || readonly}
              autoFocus={autofocus}
              onChange={(event) => onChange(event.target.checked)}
              onBlur={onBlur && ((event) => onBlur(id, event.target.checked))}
              onFocus={
                onFocus && ((event) => onFocus(id, event.target.checked))
              }
              className="mr-1"
            />
            <span>{label}</span>
          </label>
        </div>
      );
    },
  },
  fields: {
    TitleField: (props) => {
      return (
        <div className="font-mono-natto text-2xs uppercase">{props.title}</div>
      );
    },
    DescriptionField: (props) => {
      if (props.description === undefined) {
        return null;
      }
      return (
        <div className="text-neutral-500 mb-1 whitespace-pre-wrap">
          {props.description}
        </div>
      );
    },
  },
  FieldTemplate: (props) => {
    const {
      label,
      displayLabel,
      description,
      required,
      id,
      children,
      errors,
      help,
      schema,
    } = props;
    return (
      <div
        className={classNames(schema.type !== "object" ? "mb-2" : undefined)}
      >
        {displayLabel && label !== undefined ? (
          <label htmlFor={id} className="h-4 block font-mono-natto text-2xs">
            {label}
            {schema.type !== undefined ? (
              <span className="normal-case inline-block ml-1 text-neutral-400">
                {required ? "required " : ""}
                {schema.type}
              </span>
            ) : null}
          </label>
        ) : null}
        {displayLabel ? description : null}
        {children}
        {errors}
        {help}
      </div>
    );
  },
});

function TemplateMetadataEditor({
  paneId,
  template,
  metadata,
}: {
  paneId: string;
  template: EvalPaneTemplate;
  metadata: any;
}) {
  const templateSchema = useMemo(() => {
    const schema = { ...template.schema };
    delete schema.title;
    delete schema.description;
    return schema;
  }, [template.schema]);
  const [jsonSchemaValue, setJsonSchemaValue] = useState(metadata);
  const [hasChange, setHasChange] = useState(false);
  const [formIdPrefix] = useState(() => nanoid(8));

  return (
    <div className="p-2">
      {template.schema.title !== undefined ? (
        <div className="text-sm font-medium">{template.schema.title}</div>
      ) : null}
      {template.schema.description !== undefined ? (
        <div className="pt-1 whitespace-pre-wrap text-neutral-500">
          {template.schema.description}
        </div>
      ) : null}
      {Object.keys(templateSchema.properties).length > 0 ? (
        <>
          <ThemedForm
            schema={templateSchema}
            uiSchema={template.uiSchema}
            formData={jsonSchemaValue}
            idPrefix={formIdPrefix}
            onChange={(e) => {
              setJsonSchemaValue(e.formData);
              setHasChange(true);
            }}
            noValidate={true}
            noHtml5Validate={true}
            className="pt-1 mt-2 border-t border-neutral-150"
          />
          <div className="flex justify-end">
            {hasChange ? (
              <button
                onClick={() => {
                  setJsonSchemaValue(metadata);
                  setHasChange(false);
                }}
                className="text-neutral-400 hover:text-neutral-600 transition-colors mr-2 px-2"
              >
                reset
              </button>
            ) : null}
            <button
              type="submit"
              onClick={() => {
                canvasDispatch([
                  ActionType.SetPaneMetadata,
                  {
                    paneId,
                    metadata: jsonSchemaValue,
                  },
                ]);
                setHasChange(false);
              }}
              className={classNames(
                hasChange ? "button-dark" : "button"
                // hasChange ? "bg-primary-400 text-neutral-0" : "bg-neutral-150"
              )}
            >
              update
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}

type PaneEvaluateTemplateComponentProps = {
  paneId: string;
  paneComputed: IComputedValue<
    Extract<Pane, { type: PaneType.EvaluateTemplate }> | undefined
  >;
  virtualPaneInputs: PaneInput[] | undefined;
  focusOnMount: boolean;
  template: EvalPaneTemplate;
  isMaximized: boolean;
};
const emptyFunction = () => undefined;

const PaneEvaluateTemplateComponentBody = observer(
  ({
    paneId,
    paneComputed,
    virtualPaneInputs,
    focusOnMount,
    template,
    isMaximized,
  }: PaneEvaluateTemplateComponentProps) => {
    const activePaneState = activePaneStates.get(paneId)!;
    assert(activePaneState.type === PaneType.EvaluateTemplate);
    const outputBox = activePaneState.outputBoxes[0];
    const output = outputBox.get();

    const paneMetadata = computed(
      () => paneComputed.get()?.metadata ?? {}
    ).get();
    const paneTemplateInputSources = computed(
      () => paneComputed.get()?.templateInputSources ?? {}
    ).get();
    const constructedTemplate = useMemo(() => {
      return template.construct(paneMetadata);
    }, [template, paneMetadata]);

    const constructedOutputBoxes: IObservableValue<PaneOutput>[][] = useMemo(
      () =>
        constructedTemplate.panes.map((p) =>
          p.type === PaneType.State
            ? [
                observable.box(["waiting"], { deep: false }),
                observable.box(["waiting"], { deep: false }),
                observable.box(["waiting"], { deep: false }),
              ]
            : [observable.box(["waiting"], { deep: false })]
        ),
      [constructedTemplate.panes]
    );
    useEffect(() => {
      if (virtualPaneInputs === undefined) {
        return;
      }
      const unsubscribes: (() => void)[] = constructedTemplate.panes.map(
        (pane, paneIndex) => {
          const outputBox = constructedOutputBoxes[paneIndex][0];
          if (pane.type === PaneType.Evaluate) {
            const functionGet = getFunctionComputed(
              false,
              { get: () => pane.expressionType },
              { get: () => pane.expression },
              { get: () => pane.inputs },
              { get: () => pane.babelPlugins ?? [] },
              []
            );
            const evaluator = new Evaluator(
              functionGet,
              outputBox,
              false,
              { get: () => pane.inputs },
              (input) => {
                const source = input.source;
                if (Array.isArray(source)) {
                  return constructedOutputBoxes[source[0]][source[1] ?? 0];
                } else {
                  return virtualPaneInputs !== undefined
                    ? getPaneInputBox(
                        virtualPaneInputs.find(
                          (paneInput) => paneInput.id === source
                        )!
                      )
                    : undefined;
                }
              },
              { get: () => pane.babelPlugins ?? [] },
              { get: () => true },
              { get: () => false },
              {}
            );
            return evaluator.init();
          } else if (pane.type === PaneType.Import) {
            const paneModule = pane.module;
            const useDefault = pane.useDefault === true;
            let isActive = true;
            async function go() {
              if (paneModule !== undefined) {
                if (paneModule[0] === "script") {
                  const scriptElement = document.createElement("script");
                  const scriptUrl = paneModule[1];
                  scriptElement.onload = () => {
                    if (isActive) {
                      outputBox.set(["value", undefined]);
                    }
                  };
                  scriptElement.onerror = action(() => {
                    outputBox.set(["error", undefined]);
                  });
                  scriptElement.src = scriptUrl;
                  document.head.appendChild(scriptElement);
                } else {
                  const moduleId = paneModule[1];
                  const moduleUrl =
                    paneModule[0] === "npm"
                      ? paneModule[2] ?? `https://esm.sh/${moduleId}`
                      : paneModule[0] === "url"
                      ? paneModule[1]
                      : paneModule[1];
                  try {
                    const module = await import(moduleUrl);
                    if (!isActive) {
                      return;
                    }
                    runInAction(() => {
                      outputBox.set([
                        "value",
                        useDefault ? module.default : module,
                      ]);
                    });
                  } catch (e) {
                    if (isActive) {
                      runInAction(() => {
                        outputBox.set(["error", e]);
                      });
                    }
                  }
                }
              }
            }
            go();
            return () => {
              isActive = false;
            };
          } else if (pane.type === PaneType.State) {
            runInAction(() => {
              if (pane.initialExpression !== "") {
                let output: PaneOutput;
                try {
                  const f = new Function(`return ${pane.initialExpression}`);
                  output = ["value", f()];
                } catch (e) {
                  output = ["error", e];
                }
                outputBox.set(output);
              }
              const outputSetterAtom = constructedOutputBoxes[paneIndex][1];
              outputSetterAtom.set(statePaneSetterValue(outputBox));
              const outputGetterBox =
                constructedOutputBoxes[paneIndex][
                  STATE_PANE_GETTER_OUTPUT_INDEX
                ];
              outputGetterBox.set(statePaneGetterValue(outputBox));
            });
            return () => {};
          } else {
            throw new Error();
          }
        }
      );
      return () => {
        unsubscribes.forEach((unsubscribe) => {
          unsubscribe();
        });
      };
    }, [constructedTemplate, virtualPaneInputs]);
    const mainPane = constructedTemplate.panes[0];
    useEffect(
      () =>
        reaction(
          () => constructedOutputBoxes[0][0].get(),
          action((mainOutputValue) => {
            outputBox.set(mainOutputValue);
          })
        ),
      [constructedOutputBoxes]
    );

    const constructedInputs = constructedTemplate.inputs;
    useEffect(
      action(() => {
        activePaneState.paneInputs.value = constructedInputs;
        const paneInputIds = new Set(
          constructedInputs.map((paneInput) => paneInput.id)
        );
        Object.keys(paneTemplateInputSources).forEach((paneInputId) => {
          if (!paneInputIds.has(paneInputId)) {
            canvasDispatch([
              ActionType.SetPaneTemplateInputSource,
              {
                paneId,
                paneInputId,
                source: undefined,
              },
            ]);
          }
        });
      }),
      [constructedInputs]
    );

    const focusEditorOnMountRef = useRef(focusOnMount);
    useEffect(() => {
      if (focusEditorOnMountRef.current) {
        focusEditorOnMountRef.current = false;
      }
    }, [focusEditorOnMountRef.current]);

    const [editorTab, setEditorTab] = useState<"template" | "generated-panes">(
      "template"
    );
    const viewMode = activePaneState.viewMode.get();

    return (
      <PaneBody viewMode={viewMode} paneComputed={paneComputed}>
        <div className="h-full flex flex-col relative">
          <PaneSplit
            paneId={paneId}
            paneType={PaneType.EvaluateTemplate}
            activePaneState={activePaneState}
            isMaximized={isMaximized}
            inputElement={
              <div className="flex flex-col max-h-full overflow-hidden">
                <div className="flex border-b border-neutral-200 shrink-0">
                  <button
                    onClick={() => {
                      setEditorTab("template");
                    }}
                    className={classNames(
                      "px-2 py-2 font-medium transition",
                      editorTab === "template"
                        ? "text-neutral-600"
                        : "text-neutral-400 hover:text-primary-600"
                    )}
                  >
                    template
                  </button>
                  <button
                    onClick={() => {
                      setEditorTab("generated-panes");
                    }}
                    className={classNames(
                      "px-2 py-2 font-medium transition",
                      editorTab === "generated-panes"
                        ? "text-neutral-600"
                        : "text-neutral-400 hover:text-primary-600"
                    )}
                  >
                    generated pane
                    {constructedTemplate.panes.length > 1 ? "s" : null}
                  </button>
                  <div className="grow" />
                  <EjectButton paneId={paneId} />
                </div>
                <div className="grow relative overflow-auto">
                  <TemplateMetadataEditor
                    paneId={paneId}
                    template={template}
                    metadata={paneMetadata}
                  />
                </div>
              </div>
            }
            outputElement={
              <div className="h-full overflow-auto select-text relative group">
                {output[0] === "value" ? (
                  <ReactErrorBoundary>
                    {editorTab === "template" ? (
                      <PaneRenderOutputComponent
                        paneId={paneId}
                        value={output[1]}
                        renderOutput={
                          mainPane.type === PaneType.Evaluate
                            ? mainPane.renderOutput
                            : undefined
                        }
                        canChangeRenderOutput={false}
                        getRenderInput={emptyFunction}
                      />
                    ) : null}
                  </ReactErrorBoundary>
                ) : output[0] === "error" ? (
                  <div className="font-mono-natto p-2 text-red-400">
                    {output[1] !== undefined ? output[1].toString() : "error"}
                  </div>
                ) : output[0] === "waiting" && output[1] === "input" ? (
                  <div className="font-mono-natto italic p-2 text-neutral-400">
                    waiting for input
                  </div>
                ) : null}
              </div>
            }
          />
          {editorTab === "generated-panes" ? (
            <div className="absolute top-[33px] left-0 right-0 bottom-0 z-1">
              <PaneEvaluateTemplateComponentCanvas
                activePaneState={activePaneState}
                constructedTemplate={constructedTemplate}
                constructedOutputBoxes={constructedOutputBoxes}
              />
            </div>
          ) : null}
        </div>
      </PaneBody>
    );
  }
);

export const PaneEvaluateTemplateComponent = observer(
  (
    props: Omit<
      PaneEvaluateTemplateComponentProps,
      "template" | "virtualPaneInputs"
    > & {
      layoutId: string | undefined;
      virtualPaneInputsComputed: IComputedValue<PaneInput[] | undefined>;
    }
  ) => {
    const {
      paneId,
      paneComputed,
      isMaximized,
      virtualPaneInputsComputed,
      layoutId,
      ...restProps
    } = props;
    const virtualPaneInputs = virtualPaneInputsComputed.get();
    const paneTemplateId = computed(() => paneComputed.get()?.templateId).get();
    const template =
      paneTemplateId !== undefined
        ? EvalPaneTemplates[paneTemplateId]
        : undefined;

    const [showPopupPaneInputId, setShowPopupPaneInputId] = useState<
      PopupPaneInputId | undefined
    >(undefined);

    if (template === undefined) {
      return null;
    }

    return (
      <>
        <PaneHeaderBar
          paneId={paneId}
          paneComputed={paneComputed}
          isMaximized={isMaximized}
        >
          <div className="grow" />
          {!isMaximized ? <PaneTransformButton paneId={paneId} /> : null}
        </PaneHeaderBar>
        {template !== undefined ? (
          <PaneEvaluateTemplateComponentBody
            {...restProps}
            paneId={paneId}
            paneComputed={paneComputed}
            virtualPaneInputs={virtualPaneInputs}
            template={template}
            isMaximized={isMaximized}
          />
        ) : (
          <div className="p-2 text-red-400">template not found</div>
        )}
        {activePaneStates.get(paneId)?.viewMode.get() === PaneViewMode.Normal &&
        virtualPaneInputs !== undefined
          ? virtualPaneInputs.map((virtualPaneInput, inputIndex) => {
              return (
                <PaneInputNode
                  inputIndex={inputIndex}
                  paneId={paneId}
                  paneType={PaneType.EvaluateTemplate}
                  paneInputs={virtualPaneInputs}
                  paneInput={virtualPaneInput}
                  showPopupPaneInputId={showPopupPaneInputId}
                  setShowPopupPaneInputId={setShowPopupPaneInputId}
                  hideIfNotActive={isCustomLayoutId(layoutId)}
                  key={virtualPaneInput.id}
                />
              );
            })
          : null}
      </>
    );
  }
);
